/**
 * Performance Report Service
 * 
 * Generates and sends performance reports for Google Ads campaigns.
 * Integrates with Gmail service for email delivery.
 */

import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  limit as firestoreLimit,
  Timestamp
} from 'firebase/firestore';
import { googleAdsMccService } from './googleAdsMccService';
import { gmailService } from './gmailService';
import { geminiService } from './geminiService';

// ============================================================================
// TYPES
// ============================================================================

export interface PerformanceReport {
  id: string;
  orgId: string;
  customerId: string;
  createdAt: Date;
  period: 'daily' | 'weekly' | 'monthly';
  dateRange: {
    start: string;
    end: string;
  };
  
  // Account Summary
  accountSummary: {
    totalCost: number;
    totalConversions: number;
    totalClicks: number;
    totalImpressions: number;
    avgCtr: number;
    avgCpa: number;
    avgRoas: number;
    activeCampaigns: number;
  };
  
  // Campaign Details
  campaigns: CampaignReportItem[];
  
  // Top Performers
  topPerformers: {
    keywords: { text: string; ctr: number; conversions: number }[];
    campaigns: { name: string; roas: number; conversions: number }[];
  };
  
  // Recommendations
  recommendations: string[];
  
  // Comparison
  comparison?: {
    vsLastPeriod: {
      costChange: number;
      conversionsChange: number;
      roasChange: number;
    };
  };
}

export interface CampaignReportItem {
  id: string;
  name: string;
  status: string;
  type: string;
  cost: number;
  conversions: number;
  clicks: number;
  impressions: number;
  ctr: number;
  cpa: number;
  roas: number;
  trend: 'up' | 'down' | 'stable';
}

export interface ReportEmailOptions {
  recipientEmail: string;
  recipientName?: string;
  ccEmails?: string[];
  customMessage?: string;
  language?: 'de' | 'en';
}

// ============================================================================
// REPORT GENERATION
// ============================================================================

export const performanceReportService = {
  /**
   * Generate a performance report for an organization
   */
  async generateReport(
    orgId: string,
    customerId: string,
    period: 'daily' | 'weekly' | 'monthly' = 'weekly'
  ): Promise<PerformanceReport> {
    console.log(`📊 Generating ${period} performance report for ${customerId}...`);
    
    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    
    switch (period) {
      case 'daily':
        startDate.setDate(endDate.getDate() - 1);
        break;
      case 'weekly':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'monthly':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
    }
    
    const dateRangeStr = period === 'daily' 
      ? 'YESTERDAY' 
      : period === 'weekly' 
        ? 'LAST_7_DAYS' 
        : 'LAST_30_DAYS';
    
    // Fetch account overview
    let accountOverview;
    try {
      accountOverview = await googleAdsMccService.getAccountOverview(customerId, dateRangeStr);
    } catch (e) {
      console.warn('Failed to fetch account overview, using Firestore data');
      accountOverview = await this.getAccountOverviewFromFirestore(orgId, customerId);
    }
    
    // Fetch campaign data from Firestore
    const campaigns = await this.getCampaignMetrics(orgId, customerId, startDate, endDate);
    
    // Calculate aggregates
    const totalCost = campaigns.reduce((sum, c) => sum + c.cost, 0);
    const totalConversions = campaigns.reduce((sum, c) => sum + c.conversions, 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);
    const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgCpa = totalConversions > 0 ? totalCost / totalConversions : 0;
    const totalConversionValue = campaigns.reduce((sum, c) => sum + (c.roas * c.cost), 0);
    const avgRoas = totalCost > 0 ? totalConversionValue / totalCost : 0;
    
    // Get top performers
    const topCampaigns = [...campaigns]
      .sort((a, b) => b.roas - a.roas)
      .slice(0, 3)
      .map(c => ({ name: c.name, roas: c.roas, conversions: c.conversions }));
    
    const topKeywords = await this.getTopKeywords(orgId, customerId, 5);
    
    // Generate AI recommendations
    const recommendations = await this.generateRecommendations(campaigns, {
      totalCost,
      totalConversions,
      avgCtr,
      avgCpa,
      avgRoas
    });
    
    // Get comparison data
    const comparison = await this.getComparisonData(orgId, customerId, period, startDate);
    
    const report: PerformanceReport = {
      id: `report-${Date.now()}`,
      orgId,
      customerId,
      createdAt: new Date(),
      period,
      dateRange: {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      },
      accountSummary: {
        totalCost,
        totalConversions,
        totalClicks,
        totalImpressions,
        avgCtr,
        avgCpa,
        avgRoas,
        activeCampaigns: campaigns.filter(c => c.status === 'active').length
      },
      campaigns,
      topPerformers: {
        keywords: topKeywords,
        campaigns: topCampaigns
      },
      recommendations,
      comparison
    };
    
    console.log(`✅ Report generated: ${campaigns.length} campaigns, €${totalCost.toFixed(2)} spend`);
    
    return report;
  },

  /**
   * Send performance report via email
   */
  async sendReportEmail(
    userId: string,
    report: PerformanceReport,
    options: ReportEmailOptions
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    console.log(`📧 Sending performance report to ${options.recipientEmail}...`);
    
    const isGerman = options.language !== 'en';
    
    // Generate email HTML
    const emailHtml = this.generateEmailHtml(report, isGerman, options.customMessage);
    
    // Generate subject line
    const subject = isGerman
      ? `📊 Google Ads Performance Report: ${report.dateRange.start} - ${report.dateRange.end}`
      : `📊 Google Ads Performance Report: ${report.dateRange.start} - ${report.dateRange.end}`;
    
    try {
      const result = await gmailService.sendEmail(userId, {
        to: options.recipientEmail,
        subject,
        html: emailHtml,
        cc: options.ccEmails
      });
      
      console.log(`✅ Report email sent successfully`);
      return { success: true, messageId: result.messageId };
    } catch (error: any) {
      console.error('Failed to send report email:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Generate email HTML from report
   */
  generateEmailHtml(report: PerformanceReport, isGerman: boolean, customMessage?: string): string {
    const t = isGerman ? {
      title: 'Google Ads Performance Report',
      period: 'Zeitraum',
      summary: 'Zusammenfassung',
      cost: 'Ausgaben',
      conversions: 'Conversions',
      clicks: 'Klicks',
      ctr: 'CTR',
      cpa: 'CPA',
      roas: 'ROAS',
      campaigns: 'Kampagnen',
      topPerformers: 'Top Performer',
      recommendations: 'Empfehlungen',
      generatedBy: 'Generiert von Echomotion KI'
    } : {
      title: 'Google Ads Performance Report',
      period: 'Period',
      summary: 'Summary',
      cost: 'Spend',
      conversions: 'Conversions',
      clicks: 'Clicks',
      ctr: 'CTR',
      cpa: 'CPA',
      roas: 'ROAS',
      campaigns: 'Campaigns',
      topPerformers: 'Top Performers',
      recommendations: 'Recommendations',
      generatedBy: 'Generated by Echomotion AI'
    };

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 700px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #4285f4, #34a853); color: white; padding: 30px; border-radius: 12px; margin-bottom: 24px; }
    .header h1 { margin: 0 0 8px 0; font-size: 24px; }
    .header .period { opacity: 0.9; font-size: 14px; }
    .card { background: #f8f9fa; border-radius: 12px; padding: 24px; margin-bottom: 20px; }
    .card h2 { margin: 0 0 16px 0; font-size: 18px; color: #333; }
    .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .metric { text-align: center; }
    .metric .value { font-size: 28px; font-weight: 700; color: #4285f4; }
    .metric .label { font-size: 12px; color: #666; text-transform: uppercase; }
    .campaign-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e0e0e0; }
    .campaign-row:last-child { border-bottom: none; }
    .campaign-name { font-weight: 500; }
    .campaign-metrics { color: #666; font-size: 14px; }
    .recommendation { background: #fff3cd; padding: 12px 16px; border-radius: 8px; margin-bottom: 8px; border-left: 4px solid #ffc107; }
    .change-up { color: #34a853; }
    .change-down { color: #ea4335; }
    .footer { text-align: center; color: #666; font-size: 12px; margin-top: 32px; padding-top: 16px; border-top: 1px solid #e0e0e0; }
  </style>
</head>
<body>
  <div class="header">
    <h1>📊 ${t.title}</h1>
    <div class="period">${t.period}: ${report.dateRange.start} - ${report.dateRange.end}</div>
  </div>

  ${customMessage ? `<div class="card"><p>${customMessage}</p></div>` : ''}

  <div class="card">
    <h2>📈 ${t.summary}</h2>
    <div class="metrics">
      <div class="metric">
        <div class="value">€${report.accountSummary.totalCost.toFixed(0)}</div>
        <div class="label">${t.cost}</div>
      </div>
      <div class="metric">
        <div class="value">${report.accountSummary.totalConversions.toFixed(0)}</div>
        <div class="label">${t.conversions}</div>
      </div>
      <div class="metric">
        <div class="value">${report.accountSummary.avgRoas.toFixed(1)}x</div>
        <div class="label">${t.roas}</div>
      </div>
      <div class="metric">
        <div class="value">${report.accountSummary.totalClicks.toLocaleString()}</div>
        <div class="label">${t.clicks}</div>
      </div>
      <div class="metric">
        <div class="value">${report.accountSummary.avgCtr.toFixed(2)}%</div>
        <div class="label">${t.ctr}</div>
      </div>
      <div class="metric">
        <div class="value">€${report.accountSummary.avgCpa.toFixed(2)}</div>
        <div class="label">${t.cpa}</div>
      </div>
    </div>
    ${report.comparison ? `
    <div style="margin-top: 16px; font-size: 14px;">
      <span class="${report.comparison.vsLastPeriod.conversionsChange >= 0 ? 'change-up' : 'change-down'}">
        ${report.comparison.vsLastPeriod.conversionsChange >= 0 ? '↑' : '↓'} 
        ${Math.abs(report.comparison.vsLastPeriod.conversionsChange).toFixed(1)}% Conversions
      </span>
    </div>
    ` : ''}
  </div>

  <div class="card">
    <h2>🎯 ${t.campaigns}</h2>
    ${report.campaigns.slice(0, 5).map(c => `
    <div class="campaign-row">
      <div class="campaign-name">${c.name}</div>
      <div class="campaign-metrics">
        €${c.cost.toFixed(0)} | ${c.conversions.toFixed(0)} Conv | ${(c.ctr * 100).toFixed(1)}% CTR
      </div>
    </div>
    `).join('')}
  </div>

  ${report.recommendations.length > 0 ? `
  <div class="card">
    <h2>💡 ${t.recommendations}</h2>
    ${report.recommendations.map(r => `
    <div class="recommendation">${r}</div>
    `).join('')}
  </div>
  ` : ''}

  <div class="footer">
    ${t.generatedBy} • ${new Date().toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
  </div>
</body>
</html>
    `;
  },

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  async getCampaignMetrics(
    orgId: string, 
    customerId: string, 
    startDate: Date, 
    endDate: Date
  ): Promise<CampaignReportItem[]> {
    const campaignsRef = collection(db, 'googleAdsCampaigns');
    const q = query(
      campaignsRef,
      where('orgId', '==', orgId),
      where('customerId', '==', customerId)
    );
    
    const snapshot = await getDocs(q);
    const campaigns: CampaignReportItem[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      const metrics = data.metrics || {};
      
      campaigns.push({
        id: doc.id,
        name: data.name || 'Unknown',
        status: data.status || 'unknown',
        type: data.type || 'search',
        cost: metrics.cost || 0,
        conversions: metrics.conversions || 0,
        clicks: metrics.clicks || 0,
        impressions: metrics.impressions || 0,
        ctr: metrics.ctr || 0,
        cpa: metrics.cpa || 0,
        roas: metrics.roas || 0,
        trend: this.calculateTrend(metrics)
      });
    });
    
    return campaigns;
  },

  async getTopKeywords(
    orgId: string, 
    customerId: string, 
    limitCount: number
  ): Promise<{ text: string; ctr: number; conversions: number }[]> {
    try {
      const keywordsRef = collection(db, 'campaignKeywords');
      const q = query(
        keywordsRef,
        where('orgId', '==', orgId),
        where('customerId', '==', customerId),
        orderBy('syncedAt', 'desc'),
        firestoreLimit(1)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return [];
      
      const keywordDoc = snapshot.docs[0].data();
      const keywords = keywordDoc.keywords || [];
      
      return keywords
        .sort((a: any, b: any) => b.ctr - a.ctr)
        .slice(0, limitCount)
        .map((k: any) => ({
          text: k.text,
          ctr: k.ctr,
          conversions: k.conversions
        }));
    } catch (e) {
      return [];
    }
  },

  async getAccountOverviewFromFirestore(orgId: string, customerId: string): Promise<any> {
    // Fallback when API is unavailable
    const campaigns = await this.getCampaignMetrics(orgId, customerId, new Date(0), new Date());
    
    return {
      account: { customerId, name: 'Account' },
      metrics: {
        cost: campaigns.reduce((sum, c) => sum + c.cost, 0),
        conversions: campaigns.reduce((sum, c) => sum + c.conversions, 0),
        clicks: campaigns.reduce((sum, c) => sum + c.clicks, 0),
        impressions: campaigns.reduce((sum, c) => sum + c.impressions, 0)
      },
      activeCampaigns: campaigns.length
    };
  },

  async getComparisonData(
    orgId: string, 
    customerId: string, 
    period: string, 
    currentStart: Date
  ): Promise<PerformanceReport['comparison'] | undefined> {
    // Calculate previous period dates
    const previousEnd = new Date(currentStart);
    const previousStart = new Date(currentStart);
    
    switch (period) {
      case 'daily':
        previousStart.setDate(previousStart.getDate() - 1);
        break;
      case 'weekly':
        previousStart.setDate(previousStart.getDate() - 7);
        break;
      case 'monthly':
        previousStart.setMonth(previousStart.getMonth() - 1);
        break;
    }
    
    try {
      // Get previous period data from Firestore
      const prevCampaigns = await this.getCampaignMetrics(orgId, customerId, previousStart, previousEnd);
      const currCampaigns = await this.getCampaignMetrics(orgId, customerId, currentStart, new Date());
      
      const prevCost = prevCampaigns.reduce((sum, c) => sum + c.cost, 0);
      const currCost = currCampaigns.reduce((sum, c) => sum + c.cost, 0);
      const prevConv = prevCampaigns.reduce((sum, c) => sum + c.conversions, 0);
      const currConv = currCampaigns.reduce((sum, c) => sum + c.conversions, 0);
      const prevRoas = prevCampaigns.reduce((sum, c) => sum + c.roas, 0) / (prevCampaigns.length || 1);
      const currRoas = currCampaigns.reduce((sum, c) => sum + c.roas, 0) / (currCampaigns.length || 1);
      
      return {
        vsLastPeriod: {
          costChange: prevCost > 0 ? ((currCost - prevCost) / prevCost) * 100 : 0,
          conversionsChange: prevConv > 0 ? ((currConv - prevConv) / prevConv) * 100 : 0,
          roasChange: prevRoas > 0 ? ((currRoas - prevRoas) / prevRoas) * 100 : 0
        }
      };
    } catch (e) {
      return undefined;
    }
  },

  calculateTrend(metrics: any): 'up' | 'down' | 'stable' {
    // Simple trend calculation based on recent performance
    if (!metrics.lastSyncedData) return 'stable';
    
    const currentRoas = metrics.roas || 0;
    const previousRoas = metrics.lastSyncedData?.roas || 0;
    
    if (currentRoas > previousRoas * 1.1) return 'up';
    if (currentRoas < previousRoas * 0.9) return 'down';
    return 'stable';
  },

  async generateRecommendations(
    campaigns: CampaignReportItem[],
    summary: { totalCost: number; totalConversions: number; avgCtr: number; avgCpa: number; avgRoas: number }
  ): Promise<string[]> {
    const recommendations: string[] = [];
    
    // Low CTR recommendation
    if (summary.avgCtr < 2) {
      recommendations.push('CTR unter 2% - Überprüfen Sie Ihre Anzeigentexte und Keywords für bessere Relevanz');
    }
    
    // High CPA recommendation
    const avgCampaignCpa = campaigns.reduce((sum, c) => sum + c.cpa, 0) / (campaigns.length || 1);
    if (summary.avgCpa > avgCampaignCpa * 1.5) {
      recommendations.push('CPA über Durchschnitt - Prüfen Sie Keywords mit hohen Kosten und niedrigen Conversions');
    }
    
    // Low ROAS recommendation
    if (summary.avgRoas < 2) {
      recommendations.push('ROAS unter 200% - Optimieren Sie Gebote und pausieren Sie unterperformende Kampagnen');
    }
    
    // Underperforming campaigns
    const underperformers = campaigns.filter(c => c.conversions === 0 && c.cost > 50);
    if (underperformers.length > 0) {
      recommendations.push(`${underperformers.length} Kampagne(n) ohne Conversions trotz Ausgaben - Überprüfung empfohlen`);
    }
    
    // Use AI for more sophisticated recommendations if data is substantial
    if (summary.totalCost > 100 && campaigns.length > 2) {
      try {
        const aiRecommendation = await geminiService.generateWithGemini(
          `Analysiere diese Google Ads Performance und gib 1 konkrete Empfehlung (max 1 Satz):
          - Ausgaben: €${summary.totalCost.toFixed(0)}
          - Conversions: ${summary.totalConversions}
          - CTR: ${summary.avgCtr.toFixed(2)}%
          - CPA: €${summary.avgCpa.toFixed(2)}
          - ROAS: ${summary.avgRoas.toFixed(1)}x`,
          { maxOutputTokens: 100 }
        );
        if (aiRecommendation && aiRecommendation.length < 200) {
          recommendations.push(aiRecommendation);
        }
      } catch (e) {
        // Skip AI recommendation on error
      }
    }
    
    return recommendations.slice(0, 5); // Max 5 recommendations
  }
};

export default performanceReportService;

